import { navigateTo, useNuxtApp, useRequestURL } from 'nuxt/app'
import { setActivePinia } from 'pinia'

import { useAuthStore } from '@common/auth-layer/store/auth.store'
import {
  appendShowBetaPreviewToUrl,
  getShowBetaPreviewFromUrl,
  stripShowBetaPreviewFromUrl,
} from '@common/beta-preview-layer/utils/show-beta-preview-url.utils'
import { hasActiveAdminPreviewMarker } from '@common/core-layer/utils/admin-preview-cookie.utils'
import { hasAdminV2PreviewSession } from '@common/core-layer/utils/admin-preview-token.utils'
import { useVibeHost } from '@common/location-layer/composables/useVibeHost'
import { useCPNavigation } from '@common/navigation-layer/composables/useNavigationContext'
import { useCoursesAdminPreviewStore } from '@courses/admin-preview-layer/stores/courses-admin-preview.ui-store'
import {
  isCoursesAdminPreviewContext,
  isCoursesAdminPreviewCredential,
  isCoursesAdminPreviewRoute,
} from '@courses/admin-preview-layer/utils/courses-admin-preview-url.utils'
import { AUTH_PATH, getMagicLinkAuthUrl } from '@plugins/utils/magic-link'
import type { CPNavigationInput } from '@common/navigation-layer/types/navigation'
import {
  consumeAuthenticatedLoginVisit,
  CP_AUTH_EXCLUDED_PATHS_SET,
  CP_AUTH_PUBLIC_FLOW_PATHS_SET,
  isCPPublicCheckoutRoutePath,
} from '../constants/auth.constants'
import { isCPLegalRoutePath } from '../constants/legal-pages.constants'
import { normalizeLegacyLoginQuery } from '../utils/legacy-login-link.utils'
import { buildMagicLinkAuthCpNavigationInput } from '../utils/magic-link-auth-navigation.utils'

import type { Pinia } from 'pinia'
import type { RouteLocationNormalized } from 'vue-router'

/** Middleware-safe route snapshot for {@link CPNavigateTo} (avoids NUXT_E2005). */
const middlewareRouteContext = (route: RouteLocationNormalized): CPNavigationInput['currentRouteContext'] => ({
  path: route.path,
  params: route.params as Record<string, unknown>,
})

/**
 * Authentication Middleware for Clientportal App
 */
export default defineNuxtRouteMiddleware(async to => {
  if (to.meta.standalonePublicRoute === true) return

  const nuxtApp = useNuxtApp()
  const pinia = nuxtApp.$pinia as Pinia | undefined

  if (!pinia) {
    return
  }

  setActivePinia(pinia)

  const authStore = useAuthStore(pinia)
  const isAuthenticated = authStore.isLoggedIn
  const { CPNavigateTo } = useCPNavigation()
  const routeContext = middlewareRouteContext(to)

  // Courses admin preview (HMI theme builder) authenticates via an agency Bearer `token` against
  // the federated remote (`POST /user/login-token`), not clientportal magic-link/catv2 — handled by
  // `00.courses-admin-preview.global` first. Skip magic-link forwarding and the login bounce here,
  // scoped to /courses only. On reload SSR can't see the client-only acatv2 session, so we rely on
  // the non-sensitive `capm` marker cookie (`hasActiveAdminPreviewMarker`) to render the shell and
  // defer the real auth check to the client. Client-side the marker alone isn't sufficient — it
  // outlives sessionStorage (survives tab close) — so we require an actual session
  // (`hasAdminV2PreviewSession()`) or hydrated store too, otherwise a stale marker with no matching
  // `acatv2` would bypass straight into an unauthenticated `/courses` instead of hitting the login bounce below.
  const adminPreviewStore = useCoursesAdminPreviewStore(pinia)
  const ssrCookieHeader = process.server ? nuxtApp.ssrContext?.event?.node?.req?.headers?.cookie : undefined
  const isCoursesAdminPreviewActive =
    isCoursesAdminPreviewCredential(to.path, to.query) ||
    (isCoursesAdminPreviewRoute(to.path) && (adminPreviewStore.isAdminPreview || hasAdminV2PreviewSession())) ||
    (process.server && (isCoursesAdminPreviewContext(to.path, to.query) || hasActiveAdminPreviewMarker(ssrCookieHeader)))
  if (isCoursesAdminPreviewActive) {
    return
  }

  // Magic-link / login-code / session-key forward. The `show-beta-preview` flag is
  // stripped from the URL before computing `redirect=` (so it does not leak into
  // the post-login path) and re-appended to the `/auth?...` URL so it survives the
  // SSR redirect and the beta-preview-layer middleware can hydrate Pinia on /auth.
  // Hydration itself is owned by `@common/beta-preview-layer/middleware/beta-preview.global`.
  const cleanedFullPath = stripShowBetaPreviewFromUrl(to.fullPath)
  const baseAuthUrl = getMagicLinkAuthUrl(cleanedFullPath)
  if (baseAuthUrl) {
    const beta = getShowBetaPreviewFromUrl(to.fullPath)
    const authUrl = beta != null ? appendShowBetaPreviewToUrl(baseAuthUrl, beta) : baseAuthUrl
    return await CPNavigateTo(buildMagicLinkAuthCpNavigationInput(authUrl, { replace: true, redirectCode: 302 }, routeContext))
  }

  // V1 links (old affiliate emails/bookmarks) send `/login?redirectUrl=<abs URL>`; rewrite once to
  // Compass `?redirect=` so every auth screen only reads `redirect`. Runs after magic-link
  // forwarding, which owns `redirectUrl` for credentialed links.
  const legacyLoginQuery = normalizeLegacyLoginQuery(to.path, to.query, useRequestURL().origin)
  if (legacyLoginQuery) {
    return await navigateTo({ path: to.path, query: legacyLoginQuery, hash: to.hash }, { replace: true, redirectCode: 302 })
  }

  const isExcludedRoute = CP_AUTH_EXCLUDED_PATHS_SET.has(to.path)

  if (isExcludedRoute) {
    // /auth processes a magic-link / login-code / session-key credential client-side
    // and may replace the current session with a different account. Authenticated
    // users MUST be allowed to land here — otherwise we'd bounce them to /dashboard
    // before the page can run.
    if (to.path === AUTH_PATH) return

    if (isAuthenticated) {
      // Already-authenticated visit to /login or /signup with `?redirect=<path>`:
      // honour the redirect target here. Without this branch the user would land on
      // the login form indefinitely — `useAuthPage.handleLoginCallback` only fires
      // on a *fresh* login submit (via the `@login-callback` event), so an authed
      // landing has no other trigger.
      //
      // Same-origin guard mirrors `useAuth.navigateToUrl`: require `startsWith('/')`,
      // reject protocol-relative (`//evil`) and backslash variants (`/\evil`) to
      // prevent open-redirect.
      const isLoginOrSignupPath = to.path === '/login' || to.path === '/signup'
      const rawRedirect = typeof to.query.redirect === 'string' ? to.query.redirect : ''
      const isSafeInternalRedirect =
        rawRedirect.length > 0 && rawRedirect.startsWith('/') && !rawRedirect.startsWith('//') && !rawRedirect.startsWith('/\\')

      if (isLoginOrSignupPath && isSafeInternalRedirect) {
        // Path-based navigation: CPNavigateTo requires a named route, but `?redirect=`
        // can target any internal path. Use Nuxt's `navigateTo` directly with replace+302.
        return await navigateTo(rawRedirect, { replace: true, redirectCode: 302 })
      }

      // Auth-flow pages (e.g. /reset-password, /forgot-password, /login-with-code)
      // are reachable by authenticated users too — same intent as a magic-link visit
      // ("replace existing session" / "complete an emailed reset link"). The page
      // itself owns the post-action routing, so we let the navigation through.
      if (CP_AUTH_PUBLIC_FLOW_PATHS_SET.has(to.path)) return
      // One-shot bypass: AuthStatePanel "Back to login" (after a magic-link token
      // expired) sets a module flag so an authenticated user can reach /login without
      // being bounced to /dashboard. Consumed exactly once — subsequent navigations to
      // /login still bounce as normal. URL stays clean.
      if (consumeAuthenticatedLoginVisit()) return
      return await CPNavigateTo({
        name: 'clientportal-dashboard',
        currentRouteContext: routeContext,
        options: { replace: true, redirectCode: 302 },
      })
    }
    return
  }

  if (!isAuthenticated) {
    // Communities-layer pages (groups, users, conversations) are publicly accessible.
    // cpRoute meta is set only on clientportal-layer pages; communities-layer pages lack it.
    if (to.path.startsWith('/communities') && !to.meta.cpRoute) return
    if (isCPPublicCheckoutRoutePath(to.path)) return
    if (isCPLegalRoutePath(to.path)) return

    // A vibe app signs its members in on its own page.
    if (useVibeHost()) {
      return await navigateTo(`/sign-in?redirect=${encodeURIComponent(to.fullPath)}`, { external: true, replace: true, redirectCode: 302 })
    }

    const redirectPath = to.fullPath
    const loginPath = '/login'

    if (redirectPath !== loginPath && !redirectPath.startsWith(loginPath)) {
      return await CPNavigateTo({
        name: 'auth-login',
        query: { redirect: redirectPath },
        currentRouteContext: routeContext,
        options: { replace: true, redirectCode: 302 },
      })
    }

    return await CPNavigateTo({
      name: 'auth-login',
      currentRouteContext: routeContext,
      options: { replace: true, redirectCode: 302 },
    })
  }
})
